import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { identify, resetIdentity } from './analytics';
import { api, clearJwt, getJwt, setJwt } from './api';
import { getTelegram, isInTelegram } from './telegram';

export function useUser() {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.me()).user,
    staleTime: 60_000,
  });
  // Mirror identity into Sentry + PostHog whenever we have a fresh user object.
  useEffect(() => {
    if (q.data) {
      Sentry.setUser({
        id: String(q.data.id),
        username: q.data.telegramUsername ?? undefined,
      });
      identify(q.data);
    }
  }, [q.data]);
  return q;
}

export function useBootstrap() {
  const qc = useQueryClient();
  const [state, setState] = useState<'loading' | 'ready' | 'needs-dev-login' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) already have a JWT? trust it for now; React Query /api/me will revalidate
        const existing = await getJwt();
        if (existing) {
          if (!cancelled) setState('ready');
          return;
        }

        // 2) inside Telegram → exchange initData for JWT
        if (isInTelegram()) {
          const tg = getTelegram();
          if (!tg) throw new Error('telegram missing');
          const { token } = await api.authInit(tg.initData);
          await setJwt(token);
          await qc.invalidateQueries({ queryKey: ['me'] });
          if (!cancelled) setState('ready');
          return;
        }

        // 3) browser dev fallback — show the dev-login screen
        if (!cancelled) setState('needs-dev-login');
      } catch (e) {
        console.error('bootstrap failed', e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qc]);

  return { state, error };
}

export async function logout(): Promise<void> {
  resetIdentity();
  await clearJwt();
  window.location.href = '/';
}
